import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getMediaPoolItem, deleteMediaPoolItem, mediaUrl } from '../api';
import AdminDeleteControl from './AdminDeleteControl';
import MediaPreview from './MediaPreview';
import { formatBytes, formatMediaDate, mediaDisplayName } from '../utils/mediaFormat';

const USAGE_ICONS = {
  article: 'fa-regular fa-file-lines',
  news: 'fa-regular fa-newspaper',
  article_icon: 'fa-regular fa-image',
  news_icon: 'fa-regular fa-image',
  sidebar_icon: 'fa-solid fa-bars',
  platform_logo: 'fa-solid fa-palette',
  about_page: 'fa-solid fa-circle-info',
};

/** Where a usage is edited - the place an administrator would go to change it. */
function usageLink(usage) {
  switch (usage.kind) {
    case 'article':
    case 'article_icon':
      return `/article/${usage.id}`;
    case 'news':
    case 'news_icon':
      return `/news/${usage.id}`;
    case 'sidebar_icon':
    case 'platform_logo':
      return '/interface';
    case 'about_page':
      return '/about';
    default:
      return null;
  }
}

/**
 * One file of the media pool: the file itself, what is known about it, every
 * place it is used, and - when it is used nowhere - the way to delete it.
 */
function MediaPoolDetail({ mediaId, onClose, onDeleted }) {
  const { t, i18n } = useTranslation();
  const closeRef = useRef(null);

  const [media, setMedia] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let isCurrent = true;

    setMedia(null);
    setError('');

    getMediaPoolItem(mediaId)
      .then((item) => isCurrent && setMedia(item))
      .catch((loadError) => {
        if (isCurrent) {
          setError(loadError?.message || t('adminPanel.errorGeneric'));
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [mediaId, t]);

  useEffect(() => {
    closeRef.current?.focus();

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const tracks = media
    ? [
        ...media.subtitleLanguages.map((language) =>
          t('mediaPool.subtitleTrack', { language }),
        ),
        ...media.transcriptionLanguages.map((language) =>
          t('mediaPool.transcriptionTrack', { language }),
        ),
      ]
    : [];

  return (
    <div className="media-pool-detail-backdrop" onClick={onClose}>
      <aside
        className="media-pool-detail"
        role="dialog"
        aria-modal="true"
        aria-labelledby="media-pool-detail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="media-pool-detail-header">
          <h2 id="media-pool-detail-title">
            {media ? mediaDisplayName(media) : t('mediaPool.loading')}
          </h2>

          <button
            ref={closeRef}
            type="button"
            className="ui-button media-pool-close"
            aria-label={t('mediaPool.close')}
            title={t('mediaPool.close')}
            onClick={onClose}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>

        {error && (
          <div className="login-message login-message-error" role="alert">
            <i className="fa-solid fa-circle-exclamation" />
            <span>{error}</span>
          </div>
        )}

        {media && (
          <>
            <div className="media-pool-detail-preview">
              <MediaPreview media={media} controls />
            </div>

            <dl className="media-pool-facts">
              <dt>{t('mediaPool.factType')}</dt>
              <dd>{media.mimeType || '—'}</dd>

              <dt>{t('mediaPool.factSize')}</dt>
              <dd>{formatBytes(media.size, i18n.language)}</dd>

              <dt>{t('mediaPool.factUploaded')}</dt>
              <dd>{formatMediaDate(media.createdAt, i18n.language)}</dd>

              <dt>{t('mediaPool.factUploadedBy')}</dt>
              <dd>{media.uploadedBy?.userName || '—'}</dd>

              {media.description && (
                <>
                  <dt>{t('mediaPool.factDescription')}</dt>
                  <dd>{media.description}</dd>
                </>
              )}

              <dt>{t('mediaPool.factFileName')}</dt>
              <dd className="media-pool-mono">{media.fileName}</dd>

              <dt>{t('mediaPool.factHash')}</dt>
              <dd className="media-pool-mono">{media.hash}</dd>

              {tracks.length > 0 && (
                <>
                  <dt>{t('mediaPool.factTracks')}</dt>
                  <dd>{tracks.join(', ')}</dd>
                </>
              )}
            </dl>

            <a
              className="media-pool-open-link"
              href={mediaUrl(media.url)}
              target="_blank"
              rel="noreferrer"
            >
              <i className="fa-solid fa-arrow-up-right-from-square" />
              {t('mediaPool.openFile')}
            </a>

            <section className="media-pool-usages">
              <h3>
                {t('mediaPool.usagesTitle', { count: media.usages.length })}
              </h3>

              {media.usages.length === 0 ? (
                <p className="media-pool-unused-note">
                  <i className="fa-solid fa-circle-exclamation" />
                  {t('mediaPool.usedNowhere')}
                </p>
              ) : (
                <ul>
                  {media.usages.map((usage) => {
                    const to = usageLink(usage);
                    const label = t(`mediaPool.usage.${usage.kind}`);

                    return (
                      <li key={`${usage.kind}-${usage.id ?? ''}`}>
                        <i className={USAGE_ICONS[usage.kind]} />
                        <span className="media-pool-usage-kind">{label}</span>
                        {to ? (
                          <Link to={to}>{usage.title || label}</Link>
                        ) : (
                          <span>{usage.title || label}</span>
                        )}
                        {usage.isHidden && (
                          <span className="media-pool-badge">
                            {t('mediaPool.hidden')}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <AdminDeleteControl
              label={t('mediaPool.deleteFile')}
              confirmText={t('mediaPool.deleteConfirm')}
              disabledReason={
                media.usages.length > 0 ? t('mediaPool.deleteInUse') : ''
              }
              onDelete={async () => {
                await deleteMediaPoolItem(media.id);
                onDeleted([media.id]);
              }}
            />
          </>
        )}
      </aside>
    </div>
  );
}

export default MediaPoolDetail;
